import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Single source of truth for the customer portal origin. Centralized here so
// every CTA on the site opens the same URL and we can evolve the handoff in
// one place.
//
// Per-environment: `VITE_PORTAL_URL` is inlined at build time by Vite
// (CI passes it as a Docker build-arg, mirroring VITE_API_BASE — dev builds
// get https://dev-portal.openbooq.com, prod https://portal.openbooq.com).
// Defaults to PROD so a build with no config behaves exactly as before and a
// mis-wired build never hands real customers to a dev portal.
export const PORTAL_BASE_URL = (
  import.meta.env.VITE_PORTAL_URL || 'https://portal.openbooq.com'
).replace(/\/+$/, '');

// Build a portal URL, appending `?address=<urlenc>` (and optional city context)
// when the visitor has already typed/selected an address, while preserving any
// query params already present on the base path.
//
// The portal ALREADY consumes `?address=` (it geocodes the value and opens the
// wizard pre-filled), so passing it here removes the "re-type your address"
// friction on the handoff. When no address is available (e.g. header nav), we
// open the bare portal URL — no regression.
//
// NOTE: this only appends URL params. It intentionally does NOT touch the
// cross-subdomain `obq_attr` first-touch attribution cookie (see
// src/utils/firstTouch.js), which is set on `.openbooq.com` and is read by the
// portal independently of the URL — so first-touch attribution is preserved
// through the handoff regardless of whether an address is passed.
//
// `input` accepts either a plain address string, or an address-data object as
// produced by AddressAutocompleteInput / AddressSearchBar
// ({ fullAddress, address, city, state, ... }).
export function buildPortalUrl(input, opts = {}) {
  const base = opts.baseUrl || PORTAL_BASE_URL;

  // Normalize the address out of a string or address-data object.
  let address = '';
  let city = '';
  let state = '';
  if (typeof input === 'string') {
    address = input;
  } else if (input && typeof input === 'object') {
    address = input.fullAddress || input.address || input.wizardAddress || '';
    city = input.city || '';
    state = input.state || '';
  }

  if (!address || !address.trim()) return base;

  // Preserve any existing query params on the base URL.
  const [path, existingQuery = ''] = base.split('?');
  const params = new URLSearchParams(existingQuery);
  params.set('address', address.trim());
  if (city) params.set('city', city);
  if (state) params.set('state', state);

  return `${path}?${params.toString()}`;
}

// Open the customer portal. Pass the visitor's typed/selected address (string
// or address-data object) where it's in scope so it carries through to the
// portal wizard; call with no argument where there is no address yet.
//
// Back-compat: existing `openCustomerPortal()` no-arg calls keep opening the
// bare portal URL exactly as before.
export function openCustomerPortal(input, opts = {}) {
  const url = buildPortalUrl(input, opts);
  window.open(url, '_blank');
}

// Single source of truth for opening the Chatbase chat widget that's
// loaded globally in index.html. Every "open chat" button on the site
// should route through here so they all reliably trigger the same
// widget (the bottom-right floating bubble).
//
// Chatbase exposes a Proxy at window.chatbase that queues commands
// until the embed script finishes loading — `chatbase('open')` is
// safe to call immediately on page load. If for any reason the
// global proxy isn't installed (e.g. ad blocker), fall back to a
// programmatic click on the rendered bubble button in the DOM.
//
// Some legacy pages (Support, Pricing) used to dispatch a custom
// `openLiveChat` event for an internal LiveChat React component
// that's no longer rendered. All those have been re-wired to call
// this util instead.
export function openChat() {
  if (typeof window === 'undefined') return;

  if (typeof window.chatbase === 'function') {
    try {
      window.chatbase('open');
      return;
    } catch (err) {
      // fall through to DOM click
    }
  }

  const bubble =
    document.querySelector('#chatbase-bubble-button') ||
    document.querySelector('[id^="chatbase-"]') ||
    document.querySelector('button[class*="chatbase"]');
  if (bubble && typeof bubble.click === 'function') {
    bubble.click();
  }
}